import React, { Component } from 'react';
import { Redirect } from 'react-router-dom';
import queryString from 'query-string';
import './LoginPage.css';

const GOOGLE_CLIENT_ID = '189879900990-j48su5nkn32vslbc57fssmkmtsqjt7s3.apps.googleusercontent.com';
const FACEBOOK_APP_ID = '548346835996683';

class LoginForm extends Component {

  state = {
    redirect: false,
    error: false,
  }

  // Incremented on every login attempt. A response is only applied if it
  // belongs to the latest attempt, so a slow, stale request (e.g. replaying
  // an old stored token on mount) can never overwrite the result of a newer
  // one (e.g. the user clicking the Google button).
  loginSeq = 0;
  unmounted = false;

  componentDidMount() {
    // An app session already exists: this page has nothing to do, send the
    // user straight on. Previously the page stayed put, and the Google
    // callback refused to log in at all while y_jwt_token was set, so
    // clicking "Sign in with Google" silently did nothing.
    if (localStorage.getItem('y_jwt_token')) {
        this.setState({ redirect: true });
        return;
    }

    // Google Identity Services credentials are ID tokens that expire after
    // one hour and cannot be refreshed, so replaying a stored one on the
    // next visit is guaranteed to fail. Drop it; the button / One Tap
    // prompt below issues a fresh one.
    localStorage.removeItem('google_access_token');

    const facebookRedirectToken = this.consumeFacebookRedirectToken();
    if (facebookRedirectToken) {
        this.login('facebook', facebookRedirectToken, { silent: false });
    } else if (localStorage.getItem('fb_access_token')) {
        this.login('facebook', localStorage.getItem('fb_access_token'), { silent: true });
    }

    this.initializeGoogle();
  }

  componentWillUnmount() {
      this.unmounted = true;
      if (this.googleInitInterval) {
          clearInterval(this.googleInitInterval);
      }
  }

  // Handles the return trip from the Facebook OAuth dialog, which lands back
  // on this page with the access token in the URL hash. Reads it once and
  // strips it from the URL so it can't be replayed on re-render/refresh.
  consumeFacebookRedirectToken = () => {
      if (!window.location || !window.location.hash) {
          return null;
      }
      const token = queryString.parse(window.location.hash).access_token;
      if (token) {
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }
      return token || null;
  }

  initializeGoogle = () => {
      // The Google Identity Services script (index.html) loads with async/defer,
      // so window.google may not exist yet when this component mounts.
      if (this.isGoogleReady()) {
          this.renderGoogleButton();
          return;
      }
      this.googleInitInterval = setInterval(() => {
          if (this.isGoogleReady()) {
              clearInterval(this.googleInitInterval);
              this.googleInitInterval = null;
              this.renderGoogleButton();
          }
      }, 100);
  }

  isGoogleReady = () => !!(window.google && window.google.accounts && window.google.accounts.id)

  renderGoogleButton = () => {
      if (this.unmounted) {
          return;
      }
      window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: this.handleGoogleCredentialResponse,
      });
      window.google.accounts.id.renderButton(
          document.getElementById('google-sign-in'),
          { type: 'standard', theme: 'outline', size: 'large' }
      );
      window.google.accounts.id.prompt(); // also display the One Tap dialog
  }

  // A credential arriving here is always fresh and was explicitly requested
  // by the user, so always exchange it for an app session.
  handleGoogleCredentialResponse = (response) => {
      if (response && response.credential) {
          this.login('google', response.credential, { silent: false });
      } else {
          this.setState({ error: true });
      }
  }

  facebookManualLogonCallback = () => {
      let redirectUri = window.location.origin + window.location.pathname;
      window.location.href = 'https://www.facebook.com/v22.0/dialog/oauth'
          + '?client_id=' + FACEBOOK_APP_ID
          + '&response_type=token'
          + '&redirect_uri=' + encodeURIComponent(redirectUri)
          + '&scope=email';
  }

  // Exchanges a provider token for the app's own JWT.
  // silent: true for background replays of a stored token, whose failure is
  // expected (expired token) and shouldn't show an error to the user.
  login = (provider, token, { silent }) => {
      const seq = ++this.loginSeq;
      const isCurrent = () => !this.unmounted && seq === this.loginSeq;

      fetch(process.env.REACT_APP_API_BASE_URL + '/authentication/login/' + provider + '/code/' + encodeURIComponent(token), {
          method: 'post',
          headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json',
          },
      })
          .then(res => res.json()
              .catch(() => ({}))
              .then(body => ({ ok: res.ok, body })))
          .then(({ ok, body }) => {
              if (!isCurrent()) {
                  return;
              }
              const failed = !ok
                  || !body
                  || (body.data && body.data.message)
                  || body.status !== undefined
                  || !body.jwtToken;
              if (failed) {
                  this.onLoginFailed(provider, silent);
                  return;
              }
              this.onLoginSucceeded(provider, token, body.jwtToken);
          })
          .catch(error => {
              console.log('login error', error);
              if (isCurrent()) {
                  this.onLoginFailed(provider, silent);
              }
          });
  }

  onLoginSucceeded = (provider, token, jwtToken) => {
      if (provider === 'facebook') {
          this.disableGoogleAutoSelect();
          localStorage.removeItem('google_access_token');
          localStorage.setItem('fb_access_token', token);
      } else {
          localStorage.removeItem('fb_access_token');
          localStorage.setItem('google_access_token', token);
      }
      localStorage.setItem('y_jwt_token', jwtToken);
      this.setState({ redirect: true, error: false });
  }

  onLoginFailed = (provider, silent) => {
      if (provider === 'facebook') {
          localStorage.removeItem('fb_access_token');
      } else {
          this.disableGoogleAutoSelect();
          localStorage.removeItem('google_access_token');
      }
      this.setState({ redirect: false, error: !silent });
  }

  disableGoogleAutoSelect = () => {
      if (this.isGoogleReady()) {
          window.google.accounts.id.disableAutoSelect();
      }
  }

  render() {
    if (this.state.redirect) {
      return <Redirect to='/yantra' />;
    }
    return (
      <div className="login-btn login">
          <p>Connect with your social account</p>
          {this.state.error && <p className="login-error">Login failed. Please try again.</p>}
          <div className='form-group facebook_btn_div'>
              <button type='button' className='kep-login-facebook metro' onClick={this.facebookManualLogonCallback}>FACEBOOK</button>
          </div>
          <div className='login-btn form-group google_btn_div'>
            <div id="google-sign-in" className="metro">GOOGLE</div>
          </div>
          <a target='_blank' rel='noopener noreferrer' href='https://play.google.com/store/apps/details?id=com.che.yantra&pcampaignid=pcampaignidMKT-Other-global-all-co-prtnr-py-PartBadge-Mar2515-1'>
              <img className='android-logo' alt='Get it on Google Play' src='https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png'/>
          </a>
      </div>
    )
  }
}

export default LoginForm;



// WEBPACK FOOTER //
// ./src/components/LoginPage/LoginForm.js